// Merges many small static meshes into one geometry with per-vertex colour and emissive light,
// so the whole island costs a couple of draw calls instead of the mockup's 1.253.
import {
  BufferAttribute,
  BufferGeometry,
  Color,
  Euler,
  Matrix3,
  Matrix4,
  MeshStandardMaterial,
  Quaternion,
  Vector3,
  type ColorRepresentation,
} from 'three';

export interface PieceOptions {
  /** Emissive colour; defaults to none. */
  readonly emissive?: ColorRepresentation;
  readonly emissiveIntensity?: number;
  /** Keep the geometry's own smooth normals instead of faceting it. */
  readonly smooth?: boolean;
  /** Also add the back faces, for thin open shapes such as a dish. */
  readonly doubleSided?: boolean;
}

export interface Transform {
  readonly position?: readonly [number, number, number];
  readonly rotation?: readonly [number, number, number];
  readonly scale?: number | readonly [number, number, number];
}

const tmpColor = new Color();
const tmpEmissive = new Color();
const tmpVec = new Vector3();
const tmpNormal = new Vector3();

export function composeMatrix(t: Transform): Matrix4 {
  const [px, py, pz] = t.position ?? [0, 0, 0];
  const [rx, ry, rz] = t.rotation ?? [0, 0, 0];
  const s = t.scale ?? 1;
  const scale = typeof s === 'number' ? new Vector3(s, s, s) : new Vector3(s[0], s[1], s[2]);
  return new Matrix4().compose(
    new Vector3(px, py, pz),
    new Quaternion().setFromEuler(new Euler(rx, ry, rz)),
    scale,
  );
}

interface Chunk {
  readonly positions: Float32Array;
  readonly normals: Float32Array;
  readonly color: Color;
  readonly emissive: Color;
}

/**
 * Collects pieces in world space and bakes them into one non-indexed geometry.
 * A transform stack lets builders place a group of pieces (a house, a vehicle) as one unit.
 */
export class GeometryBatch {
  private readonly chunks: Chunk[] = [];
  private readonly stack: Matrix4[] = [new Matrix4()];
  private vertexCount = 0;

  private get current(): Matrix4 {
    return this.stack[this.stack.length - 1] as Matrix4;
  }

  /** Runs `build` with an extra parent transform applied to every piece it adds. */
  group(t: Transform | Matrix4, build: () => void): void {
    const local = t instanceof Matrix4 ? t : composeMatrix(t);
    this.stack.push(this.current.clone().multiply(local));
    try {
      build();
    } finally {
      this.stack.pop();
    }
  }

  add(geometry: BufferGeometry, color: ColorRepresentation, t: Transform = {}, opts: PieceOptions = {}): void {
    const source = geometry.index ? geometry.toNonIndexed() : geometry.clone();
    if (!opts.smooth) {
      source.deleteAttribute('normal');
      source.computeVertexNormals();
    } else if (!source.hasAttribute('normal')) {
      source.computeVertexNormals();
    }
    const matrix = this.current.clone().multiply(composeMatrix(t));
    const normalMatrix = new Matrix3().getNormalMatrix(matrix);
    const pos = source.getAttribute('position');
    const nor = source.getAttribute('normal');
    const positions = new Float32Array(pos.count * 3);
    const normals = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
      tmpVec.fromBufferAttribute(pos, i).applyMatrix4(matrix);
      positions.set([tmpVec.x, tmpVec.y, tmpVec.z], i * 3);
      tmpNormal.fromBufferAttribute(nor, i).applyMatrix3(normalMatrix).normalize();
      normals.set([tmpNormal.x, tmpNormal.y, tmpNormal.z], i * 3);
    }
    // A mirrored transform flips the triangle winding; restore it so faces stay front-facing.
    if (matrix.determinant() < 0) flipWinding(positions, normals);
    const emissive = opts.emissive === undefined
      ? new Color(0, 0, 0)
      : new Color(opts.emissive).multiplyScalar(opts.emissiveIntensity ?? 1);
    this.chunks.push({ positions, normals, color: new Color(color), emissive });
    this.vertexCount += pos.count;
    if (opts.doubleSided) {
      const backPositions = positions.slice();
      const backNormals = normals.map((v) => -v);
      flipWinding(backPositions, backNormals);
      this.chunks.push({ positions: backPositions, normals: backNormals, color: new Color(color), emissive });
      this.vertexCount += pos.count;
    }
    source.dispose();
  }

  /** Adds raw triangles (world space, before the current transform) with one colour. */
  addTriangles(positions: ArrayLike<number>, color: ColorRepresentation, opts: PieceOptions = {}): void {
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(Float32Array.from(positions), 3));
    this.add(geo, color, {}, opts);
    geo.dispose();
  }

  build(): BufferGeometry {
    const position = new Float32Array(this.vertexCount * 3);
    const normal = new Float32Array(this.vertexCount * 3);
    const color = new Float32Array(this.vertexCount * 3);
    const emissive = new Float32Array(this.vertexCount * 3);
    let offset = 0;
    for (const chunk of this.chunks) {
      position.set(chunk.positions, offset * 3);
      normal.set(chunk.normals, offset * 3);
      const n = chunk.positions.length / 3;
      tmpColor.copy(chunk.color);
      tmpEmissive.copy(chunk.emissive);
      for (let i = 0; i < n; i++) {
        const k = (offset + i) * 3;
        color[k] = tmpColor.r;
        color[k + 1] = tmpColor.g;
        color[k + 2] = tmpColor.b;
        emissive[k] = tmpEmissive.r;
        emissive[k + 1] = tmpEmissive.g;
        emissive[k + 2] = tmpEmissive.b;
      }
      offset += n;
    }
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(position, 3));
    geo.setAttribute('normal', new BufferAttribute(normal, 3));
    geo.setAttribute('color', new BufferAttribute(color, 3));
    geo.setAttribute('emissiveColor', new BufferAttribute(emissive, 3));
    geo.computeBoundingSphere();
    geo.computeBoundingBox();
    return geo;
  }
}

function flipWinding(positions: Float32Array, normals: Float32Array): void {
  for (let t = 0; t < positions.length; t += 9) {
    for (const arr of [positions, normals]) {
      for (let k = 0; k < 3; k++) {
        const a = arr[t + 3 + k] as number;
        arr[t + 3 + k] = arr[t + 6 + k] as number;
        arr[t + 6 + k] = a;
      }
    }
  }
}

/** Standard material that reads colour and emissive light from vertex attributes. */
export function createBatchMaterial(opts: { roughness?: number } = {}): MeshStandardMaterial {
  const material = new MeshStandardMaterial({ vertexColors: true, roughness: opts.roughness ?? 0.9, metalness: 0 });
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec3 emissiveColor;\nvarying vec3 vEmissiveColor;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvEmissiveColor = emissiveColor;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vEmissiveColor;')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += vEmissiveColor;');
  };
  material.customProgramCacheKey = () => 'islora-batch';
  return material;
}
